import type { GiftCardSummary } from '../../services/gift-cards';

export function formatGiftCardCodeDisplay(card: Pick<GiftCardSummary, 'code' | 'codeLast4'>): string {
  if (card.code && card.code.replace(/[\s-]/g, '').length >= 12) {
    return card.code;
  }
  return `•••• ${card.codeLast4}`;
}

export function formatPanGroups(card: GiftCardSummary): string {
  const display = formatGiftCardCodeDisplay(card);
  if (display.includes('•')) {
    return `••••  ••••  ••••  ${card.codeLast4}`;
  }
  const digits = display.replace(/[\s-]/g, '');
  const groups: string[] = [];
  for (let i = 0; i < digits.length; i += 4) {
    groups.push(digits.slice(i, i + 4));
  }
  return groups.join('  ');
}

const EVENT_LABELS: Record<string, string> = {
  PURCHASE: 'Issued',
  REDEMPTION: 'Redeemed',
  REFUND: 'Refund',
  VOID: 'Voided',
  ADJUSTMENT: 'Adjustment',
  ASSIGN: 'Assigned',
};

export function giftCardEventLabel(type: string): string {
  return EVENT_LABELS[type.toUpperCase()] ?? type.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

export function formatGiftCardEventAmount(type: string, amount: number): string {
  const t = type.toUpperCase();
  if (t === 'REDEMPTION' || t === 'VOID') {
    return `−$${Math.abs(amount).toFixed(2)}`;
  }
  if (t === 'PURCHASE' || t === 'REFUND') {
    return `+$${Math.abs(amount).toFixed(2)}`;
  }
  if (t === 'ASSIGN' || amount === 0) {
    return amount === 0 ? '—' : `$${Math.abs(amount).toFixed(2)}`;
  }
  const sign = amount < 0 ? '−' : '+';
  return `${sign}$${Math.abs(amount).toFixed(2)}`;
}

export function formatGiftCardBalanceAfter(value: number | string | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}

export function giftCardEventAmountIsDebit(type: string): boolean {
  return type.toUpperCase() === 'REDEMPTION' || type.toUpperCase() === 'VOID';
}

export function giftCardRoleForCustomer(card: GiftCardSummary, customerId: string): string {
  const isPurchaser = card.purchaserCustomerId === customerId;
  const isRecipient = card.recipientCustomerId === customerId;
  if (isPurchaser && isRecipient) return 'Purchaser & recipient';
  if (isRecipient) return 'Recipient';
  if (isPurchaser) return 'Purchaser';
  return '—';
}

export function isUsableGiftCard(card: GiftCardSummary): boolean {
  return card.status === 'ACTIVE' && card.balance > 0;
}
